import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import './CookingMode.css';

async function recordCooking(recipeId) {
  if (!auth.currentUser) return;
  try {
    const statsRef = doc(db, 'statistiquesRecettes', auth.currentUser.uid, 'recettes', recipeId);
    const snap = await getDoc(statsRef);
    const current = snap.exists() ? snap.data() : { etoiles: 0, nombrePreparations: 0 };
    await setDoc(statsRef, {
      ...current,
      nombrePreparations: (current.nombrePreparations || 0) + 1,
      derniereCuisson: serverTimestamp(),
    });
  } catch (err) {
    console.error('Erreur lors de la mise à jour des statistiques:', err);
  }
}

function CookingMode({ recipe, onClose }) {
  const steps = recipe.etapesPreparation || [];
  const totalSteps = steps.length;

  const [currentStep, setCurrentStep] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [timerFinished, setTimerFinished] = useState(false);
  const [showIngredients, setShowIngredients] = useState(false);
  const intervalRef = useRef(null);

  const step = steps[currentStep];

  // Réinitialiser le timer quand on change d'étape
  useEffect(() => {
    clearInterval(intervalRef.current);
    setTimerRunning(false);
    setTimerStarted(false);
    setTimerFinished(false);
    if (step && step.tempsEstime) {
      setTimerSeconds(step.tempsEstime * 60);
    } else {
      setTimerSeconds(0);
    }
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Décompte du timer
  useEffect(() => {
    if (timerRunning) {
      intervalRef.current = setInterval(() => {
        setTimerSeconds(prev => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            setTimerRunning(false);
            setTimerFinished(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [timerRunning]);

  const goNext = useCallback(() => {
    if (currentStep < totalSteps - 1) setCurrentStep(prev => prev + 1);
  }, [currentStep, totalSteps]);

  const goPrev = useCallback(() => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  }, [currentStep]);

  // Navigation au clavier
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev, onClose]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const resetTimer = () => {
    clearInterval(intervalRef.current);
    setTimerRunning(false);
    setTimerStarted(false);
    setTimerFinished(false);
    if (step && step.tempsEstime) {
      setTimerSeconds(step.tempsEstime * 60);
    }
  };

  if (totalSteps === 0) {
    return (
      <div className="cooking-overlay">
        <div className="cooking-empty">
          <p>Aucune étape de préparation enregistrée.</p>
          <button className="cooking-close-btn" onClick={onClose}>Fermer</button>
        </div>
      </div>
    );
  }

  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <div className="cooking-overlay">
      {/* Barre de progression */}
      <div className="cooking-progress-bar">
        <div className="cooking-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* En-tête */}
      <div className="cooking-header">
        <div className="cooking-recipe-name">{recipe.nom}</div>
        <button className="cooking-close-btn" onClick={onClose} aria-label="Quitter le mode cuisine">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      {/* Corps principal */}
      <div className="cooking-body">
        {/* Compteur d'étape */}
        <div className="cooking-step-counter">
          Étape {currentStep + 1} <span className="cooking-step-total">/ {totalSteps}</span>
        </div>

        {/* Description */}
        <div className="cooking-step-description">
          {step.description}
        </div>

        {/* Timer */}
        {step.tempsEstime > 0 && (
          <div className={`cooking-timer ${timerFinished ? 'timer-finished' : ''}`}>
            <div className="cooking-timer-display">
              {timerFinished ? (
                <span className="timer-done-text">Temps écoulé !</span>
              ) : (
                <span className="timer-countdown">{formatTime(timerSeconds)}</span>
              )}
            </div>
            <div className="cooking-timer-actions">
              {!timerStarted ? (
                <button
                  className="timer-btn timer-start"
                  onClick={() => { setTimerStarted(true); setTimerRunning(true); }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                  Démarrer ({step.tempsEstime} min)
                </button>
              ) : (
                <>
                  <button
                    className="timer-btn timer-toggle"
                    onClick={() => setTimerRunning(r => !r)}
                  >
                    {timerRunning ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                      </svg>
                    )}
                    {timerRunning ? 'Pause' : 'Reprendre'}
                  </button>
                  <button className="timer-btn timer-reset" onClick={resetTimer}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="1 4 1 10 7 10"/>
                      <path d="M3.51 15a9 9 0 1 0 .49-3.36"/>
                    </svg>
                    Réinitialiser
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Ingrédients (accordéon) */}
        {recipe.ingredients && recipe.ingredients.length > 0 && (
          <div className="cooking-ingredients-section">
            <button
              className="cooking-ingredients-toggle"
              onClick={() => setShowIngredients(v => !v)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
              Ingrédients
              <svg
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                style={{ transform: showIngredients ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {showIngredients && (
              <ul className="cooking-ingredients-list">
                {recipe.ingredients.map((ing, i) => (
                  <li key={i} className="cooking-ingredient-item">
                    <span className="cooking-ing-qty">{ing.quantite} {ing.unite}</span>
                    <span className="cooking-ing-name">{ing.nom}</span>
                    {ing.commentaire && <span className="cooking-ing-comment">({ing.commentaire})</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="cooking-nav">
        <button
          className="cooking-nav-btn cooking-nav-prev"
          onClick={goPrev}
          disabled={currentStep === 0}
          aria-label="Étape précédente"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Précédent
        </button>

        <div className="cooking-dots">
          {steps.map((_, i) => (
            <button
              key={i}
              className={`cooking-dot ${i === currentStep ? 'dot-active' : i < currentStep ? 'dot-done' : ''}`}
              onClick={() => setCurrentStep(i)}
              aria-label={`Étape ${i + 1}`}
            />
          ))}
        </div>

        {currentStep < totalSteps - 1 ? (
          <button
            className="cooking-nav-btn cooking-nav-next"
            onClick={goNext}
            aria-label="Étape suivante"
          >
            Suivant
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        ) : (
          <button
            className="cooking-nav-btn cooking-nav-finish"
            onClick={() => { recordCooking(recipe.id); onClose(); }}
          >
            Terminer
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default CookingMode;
